import { useQuery } from '@apollo/client';
import type { WorkflowConfiguration, WorkflowType, TopicAction } from '@communities/content-management/domain';
import { GET_COMMUNITY_GROUP_WORKFLOWS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

interface CommunityGroupWorkflowsResponse {
  communityGroup: {
    id: string;
    workflowConfigurations: WorkflowConfiguration[];
  } | null;
}

export function useCommunityGroupWorkflows(communityGroupId?: string, type?: WorkflowType) {
  const { data, loading, error, refetch } = useQuery<CommunityGroupWorkflowsResponse>(GET_COMMUNITY_GROUP_WORKFLOWS, {
    variables: { communityGroupId },
    fetchPolicy: 'cache-first',
    skip: !communityGroupId,
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const allWorkflows: WorkflowConfiguration[] = useMemo(() => {
    const rawWorkflows = data?.communityGroup?.workflowConfigurations || [];

    return rawWorkflows
      .filter(workflow => workflow && workflow.type)
      .map(workflow => ({
        type: workflow.type,
        topicActions: (workflow.topicActions || []).map((topicAction: TopicAction) => ({
          topicName: topicAction?.topicName || '',
          actionNames: topicAction?.actionNames || [],
        })),
      }));
  }, [data]);

  const workflows: WorkflowConfiguration | null = type ? allWorkflows.find(wf => wf.type === type) || null : null;

  return {
    workflows,
    loading,
    errors,
    refetch,
  };
}

export default useCommunityGroupWorkflows;
